import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity, SORT_ORDER } from '@common/types';

export type PersistedTimeOffColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialTimeOffColumns: PersistedTimeOffColumn[] = [
  {
    field: 'user',
    headerName: 'User',
    minWidth: 220,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'role',
    headerName: 'Role',
    minWidth: 120,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'start_date',
    headerName: 'Start date',
    minWidth: 130,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'end_date',
    headerName: 'End date',
    minWidth: 130,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'duration',
    headerName: 'Duration',
    minWidth: 110,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 110,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 150,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'action',
    headerName: '',
    width: 100,
    minWidth: 100,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
  },
];

export const initialTimeOffPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

export type TimeOffColumnNames =
  'user' | 'role' | 'start_date' | 'end_date' | 'duration' | 'type' | 'status';

export interface ITimeOffTableFilters {
  sort_by: TimeOffColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
  only_upcoming?: boolean;
  start_date?: string;
  end_date?: string;
  user_ids?: string[];
}

const getDefaultFilters = (): ITimeOffTableFilters => ({
  sort_order: 'desc',
  sort_by: 'user',
  page: 1,
  limit: 100,
  only_upcoming: false,
  start_date: '',
  end_date: '',
  user_ids: [],
});

interface ITimeOffTableStore {
  filters: ITimeOffTableFilters;
  setFilters: (newFilters: Partial<ITimeOffTableFilters>) => void;
  clearStore: () => void;

  columns: PersistedTimeOffColumn[];
  setColumns: (columns: PersistedTimeOffColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
}

export const useTimeOffTableStore = create<ITimeOffTableStore>()(
  persist(
    set => ({
      filters: getDefaultFilters(),
      setFilters: newFilters =>
        set(state => ({
          filters: { ...state.filters, ...newFilters },
        })),
      clearStore: () => set({ filters: getDefaultFilters() }),

      columns: initialTimeOffColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialTimeOffPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialTimeOffColumns,
          pinnedColumns: initialTimeOffPinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'time-off-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
      }),
    },
  ),
);
